import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { IPeriodLockCustomerCell, IPeriodLockUserCell, TPeriodLockRecords } from './interfaces';
import {
  PERIOD_LOCK_TABLE_KEY,
  PERIOD_LOCK_CUSTOMER_ID_KEY,
  PERIOD_LOCK_STATUS_KEY,
  PERIOD_LOCK_MODIFIED_AT_KEY,
  PERIOD_LOCK_MODIFIED_BY_KEY,
  PERIOD_LOCK_DATE_FROM_KEY,
  PERIOD_LOCK_DATE_TO_KEY,
  PERIOD_LOCK_ACTIONS_KEY,
  PERIOD_LOCK_LOCK_DATE_KEY,
} from './constants';
import { TABLE_PERIOD_LOCK } from '../../Services/TableService';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import CustomerCell from '../CmdTable/TableCells/CustomerCell';
import DateTimeCell from '../CmdTable/TableCells/DateTimeCell';
import ThSimpleSelect from '../CmdTable/TableHeadCell/ThSimpleSelect';
import ThMultiSelect from '../CmdTable/TableHeadCell/ThMultiSelect';
import ThDateTime from '../CmdTable/TableHeadCell/ThDateTime';
import TextCell from '../CmdTable/TableCells/TextCell';
import SimpleChipsCell from '../CmdTable/TableCells/SimpleChipsCell';
import MultiIconAction from './RowAction/MultiIconAction';
import { selectedCustomersAtom } from '../../store/recoil/customers';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
} from '../CmdTable/CmdTableState';

interface IUsePeriodLock {
  columns: ColumnDef<TPeriodLockRecords>[];
  columnOrder: string[];
  columnVisibility: { [key: string]: boolean };
}

export default function usePeriodLog(
  handleForm = (data: TPeriodLockRecords) => {
    console.info('table action');
  }
): IUsePeriodLock {
  const { formatMessage: f } = useIntl();
  const queryClient = useQueryClient();
  // const tableFiltersKey = useRecoilValue(CmdTableState.tableFilters);
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(PERIOD_LOCK_TABLE_KEY)
  );
  const selectedCustomers = useRecoilValue(selectedCustomersAtom);
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(PERIOD_LOCK_TABLE_KEY)
  );
  const columnsTable = useRecoilValue<ICmdTableColumn[]>(columnsAtom);

  const handleIndeterminateChange = (table) => {
    if (table.getIsSomeRowsSelected()) {
      // If currently indeterminate, unselect all.
      table.toggleAllRowsSelected(false);
    } else {
      // Otherwise, follow the default behavior.
      table.toggleAllRowsSelected(!table.getIsAllRowsSelected());
    }
  };

  const updateTable = React.useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [PERIOD_LOCK_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
  }, [queryClient, filterForRequest, sortFilter, selectedCustomers]);

  // const handleRemove = React.useCallback(
  //   async (data: TPeriodLockRecords) => {
  //     const ids = { [data.customer.id]: [data.id] };
  //     const response = await ApiService.put(`/v1/${TABLE_PERIOD_LOCK}/remove`, { ids }, true);
  //     if (response.data && response.data.status === 'ok') {
  //       updateTable();
  //     }
  //   },
  //   [updateTable]
  // );

  const handleAction = React.useCallback(
    (data: TPeriodLockRecords) => {
      handleForm(data);
      // navigate(`${TABLE_PERIOD_LOCK}?customerId=${data.customer.id}`);
    },
    [handleForm]
  );

  const columns = React.useMemo<ColumnDef<TPeriodLockRecords>[]>(
    () => [
      // {
      //   id: 'select',
      //   header: ({ table }) => (
      //     <IndeterminateCheckbox
      //       {...{
      //         checked: table.getIsAllRowsSelected(),
      //         indeterminate: table.getIsSomeRowsSelected(),
      //         // onChange: table.getToggleAllRowsSelectedHandler(),
      //         onChange: () => handleIndeterminateChange(table),
      //       }}
      //     />
      //   ),
      //   cell: ({ row }) => (
      //     <div className="px-1">
      //       <IndeterminateCheckbox
      //         {...{
      //           checked: row.getIsSelected(),
      //           disabled: !row.getCanSelect(),
      //           indeterminate: row.getIsSomeSelected(),
      //           onChange: row.getToggleSelectedHandler(),
      //         }}
      //       />
      //     </div>
      //   ),
      //   size: 30,
      // },

      {
        accessorKey: PERIOD_LOCK_CUSTOMER_ID_KEY,
        header: (table) => (
          <ThSimpleSelect
            tableName={PERIOD_LOCK_TABLE_KEY}
            id={PERIOD_LOCK_CUSTOMER_ID_KEY}
            title={f({ id: `table_list.columns.customerId` })}
            // tooltip={f({ id: `table_list.columns.${PERIOD_LOCK_CUSTOMER_ID_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.customer ? (
            <CustomerCell customer={info.getValue<IPeriodLockCustomerCell>()} />
          ) : (
            <TextCell text="" />
          ),
        // size: 260,
        minSize: 150,
      },
      {
        accessorKey: PERIOD_LOCK_DATE_FROM_KEY,
        header: (table) => (
          <ThDateTime
            id={PERIOD_LOCK_DATE_FROM_KEY}
            tableName={PERIOD_LOCK_TABLE_KEY}
            title={f({ id: `table_list.columns.${PERIOD_LOCK_DATE_FROM_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PERIOD_LOCK_DATE_FROM_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: PERIOD_LOCK_DATE_TO_KEY,
        header: (table) => (
          <ThDateTime
            id={PERIOD_LOCK_DATE_TO_KEY}
            tableName={PERIOD_LOCK_TABLE_KEY}
            title={f({ id: `table_list.columns.${PERIOD_LOCK_DATE_TO_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PERIOD_LOCK_DATE_TO_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: PERIOD_LOCK_LOCK_DATE_KEY,
        header: (table) => (
          <ThDateTime
            id={PERIOD_LOCK_LOCK_DATE_KEY}
            tableName={PERIOD_LOCK_TABLE_KEY}
            title={f({ id: `table_list.columns.lockDate` })}
            // tooltip={f({ id: `table_list.columns.${PERIOD_LOCK_DEFINITION_ID}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.lockDate ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: PERIOD_LOCK_STATUS_KEY,
        header: (table) => (
          <ThMultiSelect
            tableName={PERIOD_LOCK_TABLE_KEY}
            id={PERIOD_LOCK_STATUS_KEY}
            title={f({ id: `table_list.columns.${PERIOD_LOCK_STATUS_KEY}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_PERIOD_LOCK_STATUS_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.status ? (
            <SimpleChipsCell data={info.getValue<string>()} align="left" />
          ) : (
            <TextCell text="" />
          ),
        size: 160,
      },
      {
        accessorKey: PERIOD_LOCK_MODIFIED_BY_KEY,
        header: (table) => (
          <ThMultiSelect
            tableName={PERIOD_LOCK_TABLE_KEY}
            id={PERIOD_LOCK_MODIFIED_BY_KEY}
            title={f({ id: `table_list.columns.${PERIOD_LOCK_MODIFIED_BY_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PERIOD_LOCK_MODIFIED_BY_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <TextCell text={info.getValue<IPeriodLockUserCell>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: PERIOD_LOCK_MODIFIED_AT_KEY,
        header: (table) => (
          <ThDateTime
            id={PERIOD_LOCK_MODIFIED_AT_KEY}
            tableName={PERIOD_LOCK_TABLE_KEY}
            title={f({ id: `table_list.columns.${PERIOD_LOCK_MODIFIED_AT_KEY}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_PERIOD_LOCK_MODIFIED_AT_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },

      {
        accessorKey: PERIOD_LOCK_ACTIONS_KEY,
        header: () => (
          <div className="w-sm text-center th-sticky sticky-col detail">
            {f({ id: 'table.detailBtn' })}
          </div>
        ),
        cell: (info) =>
          info.row.original ? (
            <MultiIconAction<TPeriodLockRecords>
              data={info.row.original}
              tableName={TABLE_PERIOD_LOCK}
              onClick={handleAction}
              // tooltip={f({ id: 'table.detailBtn' })}
            />
          ) : (
            ''
          ),
        size: 60, // Adjusted for consistency with header styling
        enablePinning: true,
      },
    ],
    [f, handleAction]
  );

  const columnOrder = React.useMemo(() => {
    const order = columnsTable.map((item) => item.key);
    // add the detail and delete columns to the end
    order.push(PERIOD_LOCK_ACTIONS_KEY);
    return order;
  }, [columnsTable]);

  const columnVisibility = React.useMemo(() => {
    return columnsTable.reduce((acc, column) => {
      acc[column.key] = !column.hidden;
      return acc;
    }, {});
  }, [columnsTable]);

  return {
    columns,
    columnOrder,
    columnVisibility,
  };
}
